import { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Card, Spinner, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog, StatusVariant } from '@factorialco/f0-react'
import {
  DetailsItemType,
  DetailsItemsList,
  OneEmptyState,
  ResourceHeader,
  RichTextDisplay,
} from '@factorialco/f0-react/dist/experimental'
import { AtsHiringPlanRequisitionDetailDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { FORMAT, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'

import { RequestActions } from './RequestActions'

const statusToVariant: Record<string, StatusVariant> = {
  pending: 'warning',
  approved: 'positive',
  cancelled: 'neutral',
  rejected: 'critical',
}

const statusLabel = (status: string) => {
  return {
    pending: i18n.t('ats.vacancies_requisition_page.status.pending'),
    approved: i18n.t('ats.vacancies_requisition_page.status.approved'),
    cancelled: i18n.t('ats.vacancies_requisition_page.status.cancelled'),
    rejected: i18n.t('ats.vacancies_requisition_page.status.rejected'),
  }[status]
}

const HiringPlanRequisitionDetailPage = () => {
  const params = useParams()
  const requisitionId = String(params.requisition_id)
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data, loading } = useQuery(AtsHiringPlanRequisitionDetailDocument, {
    variables: { id: requisitionId },
  })

  const requisition = data?.ats.requisition || undefined

  const roleName = requisition?.jobCatalogTreeRole?.name
  const levelName = requisition?.jobCatalogTreeLevel?.name

  const {
    access: { id: currentAccessId },
    company,
  } = useCurrentSession()

  const showFooterActions = () => {
    if (!requisition) return false
    const isApprover = requisition.currentApproverIds?.includes(currentAccessId)
    const isAuthorOrApprover = requisition.author.id === currentAccessId || isApprover
    return requisition.status === 'pending' && isAuthorOrApprover
  }

  const handleClose = () => navigate(navigation.ats.hiringPlan.requisitions.fullPath())

  const formatSalary = useCallback(
    (valueInCents: number) =>
      i18n.formatNumber(valueInCents / 100, 'currency', company.currency ?? undefined),
    [company.currency]
  )

  const items = useMemo((): DetailsItemType[] => {
    if (!requisition) return []

    const result: DetailsItemType[] = []

    result.push({
      content: { text: requisition.jobPostingTitle, type: 'item' },
      title: i18n.t('ats.vacancies_requisition_page.requisition_detail.vacancy_name'),
    })

    if (requisition.team)
      result.push({
        content: [
          {
            action: {
              type: 'navigate',
              href: navigation.employees.teams.id.fullPath({ id: requisition.team.id }),
            },
            type: 'team',
            name: requisition.team.name,
          },
        ],
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.team'),
      })

    result.push({
      content: { text: requisition.workplace?.city ?? '', type: 'item' },
      title: i18n.t('ats.vacancies_requisition_page.requisition_detail.workplace'),
    })

    result.push({
      content: {
        text:
          requisition.reason === 'new_position'
            ? i18n.t('ats.vacancies_requisition_page.requisition_detail.reasons.new_position')
            : i18n.t('ats.vacancies_requisition_page.requisition_detail.reasons.backfill'),
        type: 'item',
      },
      title: i18n.t('ats.vacancies_requisition_page.requisition_detail.reason'),
    })

    if (requisition.lineManager)
      result.push({
        content: {
          action: {
            type: 'navigate',
            href: navigation.employees.id.fullPath({ id: requisition.lineManager.id }),
          },
          type: 'person',
          avatarUrl: buildUrl(requisition.lineManager.avatar?.url ?? ''),
          firstName: requisition.lineManager.firstName ?? '',
          lastName: requisition.lineManager.lastName ?? '',
        },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.line_manager'),
      })

    if (requisition.numberOfPositions != null)
      result.push({
        content: { text: String(requisition.numberOfPositions), type: 'item' },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.number_of_vacancies'),
      })

    if (requisition.startDate)
      result.push({
        content: {
          text: stringToDate(requisition.startDate, 'backend').format(FORMAT.ui),
          type: 'item',
        },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.start_date'),
      })

    if (requisition.salaryFrom != null)
      result.push({
        content: { text: formatSalary(requisition.salaryFrom), type: 'item' },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.salary_from'),
      })

    if (requisition.salaryTo != null)
      result.push({
        content: { text: formatSalary(requisition.salaryTo), type: 'item' },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.salary_to'),
      })

    if (requisition.salaryPeriod)
      result.push({
        content: { text: requisition.salaryPeriod, type: 'item' },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.salary_period'),
      })

    if (requisition.contractType)
      result.push({
        content: { text: requisition.contractType, type: 'item' },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.contract_type'),
      })

    if (requisition.scheduleType)
      result.push({
        content: { text: requisition.scheduleType, type: 'item' },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.schedule_type'),
      })

    if (roleName)
      result.push({
        content: { text: roleName, type: 'item' },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.role'),
      })

    if (levelName)
      result.push({
        content: { text: levelName, type: 'item' },
        title: i18n.t('ats.vacancies_requisition_page.requisition_detail.level'),
      })

    return result
  }, [requisition, navigation, formatSalary, roleName, levelName])

  const additionalData: Array<{ id: string; label: string; value: string }> = []
  if (requisition?.status === 'rejected' && requisition.materializedApprovalFlow?.reason) {
    additionalData.push({
      id: 'rejection_reason',
      label: i18n.t('ats.vacancies_requisition_page.requisition_detail.rejection_reason'),
      value: requisition.materializedApprovalFlow.reason,
    })
  }

  const statusText = requisition ? statusLabel(requisition.status) : undefined
  const statusVariant = requisition ? statusToVariant[requisition.status] : undefined

  return (
    <F0Dialog position="right" isOpen onClose={handleClose}>
      {loading && (
        <Box padding="s40" width="full" alignItems="center" flexGrow>
          <Spinner />
        </Box>
      )}
      {!loading && !requisition && (
        <OneEmptyState
          variant="warning"
          title={i18n.t('ats.vacancies_requisition_page.requisition_detail.could_not_load')}
        />
      )}
      {!loading && requisition && (
        <Box gap="s24" flexDirection="column">
          <ResourceHeader
            title={requisition?.jobPostingTitle}
            status={
              statusText && statusVariant
                ? {
                    label: statusText,
                    text: statusText,
                    variant: statusVariant,
                  }
                : undefined
            }
          />
          <Box paddingBottom="s24">
            <ApprovalsInformationBlock
              resourceId={requisitionId}
              resourceType="Ats::Requisition"
              newLook
            />
          </Box>
          <DetailsItemsList
            details={items}
            title={i18n.t('ats.vacancies_requisition_page.requisition_detail.details')}
            tableView
          />
          {requisition.note && (
            <Card>
              <Card.Content>
                <Box gap="s12" flexDirection="column">
                  <Box width="s140">
                    <Text.Normal size="200">
                      {i18n.t('ats.vacancies_requisition_page.requisition_detail.note')}
                    </Text.Normal>
                  </Box>
                  <RichTextDisplay content={requisition.note} />
                </Box>
              </Card.Content>
            </Card>
          )}
          {additionalData.map((item) => (
            <Card key={item.id}>
              <Card.Content>
                <Box gap="s12" flexDirection="column" key={item.id}>
                  <Box width="s140">
                    <Text.Normal size="200">{item.label}</Text.Normal>
                  </Box>
                  <Text.Normal weight="medium" size="200" color="grey1000">
                    {item.value}
                  </Text.Normal>
                </Box>
              </Card.Content>
            </Card>
          ))}
          {showFooterActions() && (
            <RequestActions requisitionId={requisition.id} authorId={requisition.author.id} />
          )}
        </Box>
      )}
    </F0Dialog>
  )
}

export default HiringPlanRequisitionDetailPage
